/**
 * Office.js integration for the Norican I2Q task pane: the ONLY place that
 * touches the Office host.
 *
 * Scope on purpose: this reads exactly `Office.context.mailbox.item`: the single
 * message the user has open, in the user's OWN Outlook session, and nothing else.
 * There is no Graph call, no other-mailbox access, and there is no write path:
 * the pane requests only the `ReadItem` permission (see public/addin/manifest.xml)
 * and this module never calls a setter, `saveAsync`, `body.setAsync`,
 * `displayReplyFormAsync`, `displayNewMessageForm`, or any send API. It reads
 * subject + body + sender + `internetMessageId`, hands them to our own read-only
 * API, and stops there. Shadow mode: the pane has no compose/reply/send path.
 *
 * We deliberately avoid the full `@types/office-js` dependency (it is a large type
 * surface and the DS-vendoring rules keep the dependency set tight). Instead we
 * describe the tiny subset of Office.js we actually call, and read the real enum
 * members off the runtime `Office` global the CDN script installs, so we never
 * hard-code Office's magic string values.
 */

/** One `getAsync`/`onReady` result. `status`/`host` are compared to the runtime enum members, so they stay `unknown`. */
interface OfficeAsyncResult<T> {
  status: unknown;
  value: T;
  error?: { name: string; message: string; code: number };
}

/** Sender details Outlook exposes on a read-mode message item. */
interface OfficeEmailAddressDetails {
  emailAddress?: string;
  displayName?: string;
}

interface OfficeBody {
  getAsync(coercionType: unknown, callback: (result: OfficeAsyncResult<string>) => void): void;
}

/** The read-mode item surface we rely on: all optional, all read. */
interface OfficeItem {
  subject?: string;
  /** RFC 5322 Message-ID of the open message (read mode). The join key to the case store. */
  internetMessageId?: string;
  body?: OfficeBody;
  from?: OfficeEmailAddressDetails;
  itemType?: unknown;
}

interface OfficeOnReadyInfo {
  host: unknown;
  platform: unknown;
}

/** The subset of the runtime `Office` global this module uses. */
interface OfficeJs {
  onReady(callback?: (info: OfficeOnReadyInfo) => void): Promise<OfficeOnReadyInfo>;
  context?: { mailbox?: { item?: OfficeItem } };
  HostType?: { Outlook?: unknown };
  CoercionType?: { Text?: unknown };
  AsyncResultStatus?: { Succeeded?: unknown };
}

/** Where Outlook loads the Office.js runtime from (the only external script the pane needs). */
const OFFICE_JS_URL = "https://appsforoffice.microsoft.com/lib/1/hosted/office.js";

/** Give a slow/blocked CDN a bounded wait before falling back to standalone/demo mode. */
const LOAD_TIMEOUT_MS = 6000;

function getOfficeGlobal(): OfficeJs | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return (window as unknown as { Office?: OfficeJs }).Office;
}

/**
 * Load the Office.js runtime, if it is not already present, and resolve `true`
 * once the `Office` global is usable. Resolves `false` on error or timeout so a
 * plain-browser load (or a blocked CDN) degrades to demo mode instead of hanging.
 */
function ensureOfficeJs(): Promise<boolean> {
  const existingGlobal = getOfficeGlobal();
  if (existingGlobal?.onReady) {
    return Promise.resolve(true);
  }
  if (typeof document === "undefined") {
    return Promise.resolve(false);
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (!settled) {
        settled = true;
        resolve(ok && Boolean(getOfficeGlobal()?.onReady));
      }
    };

    const timer = window.setTimeout(() => finish(false), LOAD_TIMEOUT_MS);
    const onLoad = () => {
      window.clearTimeout(timer);
      finish(true);
    };
    const onError = () => {
      window.clearTimeout(timer);
      finish(false);
    };

    const selector = `script[data-office-js="true"]`;
    const existingScript = document.head.querySelector<HTMLScriptElement>(selector);
    if (existingScript) {
      existingScript.addEventListener("load", onLoad, { once: true });
      existingScript.addEventListener("error", onError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = OFFICE_JS_URL;
    script.async = true;
    script.dataset.officeJs = "true";
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    document.head.appendChild(script);
  });
}

/** The mail the pane read from the open Outlook item. */
export interface OfficeMail {
  /** RFC 5322 Message-ID, used to look the mail up in the case store; null if unavailable. */
  internetMessageId: string | null;
  subject: string;
  body: string;
  senderEmail: string | null;
  senderName: string | null;
}

/**
 * The result of probing the Office host:
 *   - `standalone`: not running inside Outlook (plain browser / demo), or no
 *     readable message is open. The pane keeps its mock behaviour.
 *   - `item`: a read message is open; its subject/body/sender are in hand.
 */
export type OfficeProbe = { kind: "standalone" } | ({ kind: "item" } & OfficeMail);

/** Read the item body as plain text (never HTML/OOXML, we only need the words). */
function readBodyText(office: OfficeJs, body: OfficeBody): Promise<string> {
  return new Promise<string>((resolve) => {
    try {
      body.getAsync(office.CoercionType?.Text, (result) => {
        const ok = result.status === office.AsyncResultStatus?.Succeeded;
        resolve(ok && typeof result.value === "string" ? result.value : "");
      });
    } catch {
      resolve("");
    }
  });
}

/**
 * Probe the Office host and, if a read message is open, read it.
 *
 * READ-ONLY end to end: loads Office.js, waits for `onReady`, and, only when the
 * host is Outlook and an item is present, reads subject + plain-text body + the
 * sender address. It writes nothing to the mailbox and touches no other item.
 * Every failure mode (not an Office host, no item, read error) resolves to
 * `standalone` so the route still renders.
 */
export async function probeOfficeItem(): Promise<OfficeProbe> {
  const available = await ensureOfficeJs();
  if (!available) {
    return { kind: "standalone" };
  }

  const office = getOfficeGlobal();
  if (!office?.onReady) {
    return { kind: "standalone" };
  }

  const info = await office.onReady();
  const outlook = office.HostType?.Outlook;
  // In a non-Office host, `host` is null; only proceed for Outlook.
  if (info.host == null || (outlook != null && info.host !== outlook)) {
    return { kind: "standalone" };
  }

  const item = office.context?.mailbox?.item;
  if (!item || !item.body) {
    // Compose mode, an appointment without a body, or nothing selected.
    return { kind: "standalone" };
  }

  const body = await readBodyText(office, item.body);
  const from = item.from;

  return {
    kind: "item",
    internetMessageId:
      typeof item.internetMessageId === "string" && item.internetMessageId.trim() !== ""
        ? item.internetMessageId.trim()
        : null,
    subject: typeof item.subject === "string" ? item.subject : "",
    body,
    senderEmail: from?.emailAddress ?? null,
    senderName: from?.displayName ?? null,
  };
}
